import * as React from 'react';
import { TCheckout } from './Checkout.types';
import './Checkout.styled.scss';
import { CheckoutProgress } from './components/CheckoutProgress/CheckoutProgress';
import { CartReview } from './components/CartReview/CartReview';
import { ShippingForm } from './components/ShippingForm/ShippingForm';
import { PaymentMethod } from './components/PaymentMethod/PaymentMethod';
import {
  TCheckoutStep,
  TShippingFormData,
  TPaymentMethod,
} from './Checkout.types';
import { DELIVERY_METHODS } from './Checkout.constants';
import { useNavigate } from 'react-router';
import { APP_ROUTES } from '../../constants/routes';
import { useLocalCart } from '../../hooks/useLocalCart';
import { useCheckoutSession } from 'hooks/useCheckoutSession';
import {
  getCheckoutSessionRemainingMs,
  isCheckoutSessionExpired,
} from 'store/checkoutSession';
import { useAppDispatch } from 'store/store';
import { pushNotification } from 'store/notifications/notifications.slices';
import { NOTIFICATION_TYPES } from 'store/notifications/notifications.types';
import {
  abortStockReservationAsyncThunk,
  createStockReservationAsyncThunk,
} from 'store/stock/stock.action';
import {
  addItemToLocalCart,
  clearCartInLocalCart,
  TLocalCartItem,
} from 'store/localCart';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const Checkout: TCheckout = () => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    items: cartItems,
    total: subtotal,
    updateQuantity,
    removeItem,
    clearAll,
  } = useLocalCart();
  const {
    session,
    startSession,
    updateData: updateSessionData,
    updateStep: updateSessionStep,
    clearSession,
    refreshFromStorage,
  } = useCheckoutSession();
  const [currentStep, setCurrentStep] = React.useState<TCheckoutStep>(
    session?.checkoutData.currentStep || 'cart'
  );

  const [shippingData, setShippingData] =
    React.useState<TShippingFormData | null>(null);
  const [paymentMethod, setPaymentMethod] =
    React.useState<TPaymentMethod | null>(null);
  const hasHydratedSessionRef = React.useRef(false);

  const shippingCost = React.useMemo(() => {
    if (!shippingData) return 0;
    const method = DELIVERY_METHODS.find(
      (m) => m.id === shippingData.deliveryMethod
    );
    return method?.price || 0;
  }, [shippingData]);

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + delta);
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
    if (cartItems.length === 1) {
      // If last item removed, redirect to home
      navigate(APP_ROUTES.HOME);
    }
  };

  const handleCartContinue = (stockItemsInCart: Record<string, number>) => {
    if (cartItems.length === 0) return;
    dispatch(
      createStockReservationAsyncThunk({
        body: {
          stockItems: Object.entries(stockItemsInCart).map(
            ([id, quantity]) => ({ id, quantity })
          ),
        },
        onSuccess: (reservationId) => {
          startSession({
            cartItems,
            shippingData,
            paymentMethod,
            currentStep: 'shipping',
            reservationId,
          });
          dispatch(
            pushNotification({
              message: localize('checkout.notification.reserved') as string,
              type: NOTIFICATION_TYPES.INFO,
            })
          );
          setCurrentStep('shipping');
        },
      })
    );
  };

  const handleShippingContinue = (data: TShippingFormData) => {
    setShippingData(data);
    updateSessionData({
      cartItems,
      shippingData: data,
      paymentMethod,
      currentStep: 'payment',
    });
    updateSessionStep('payment');
    setCurrentStep('payment');
  };

  const handleShippingBack = () => {
    clearSession();
    if (session?.checkoutData.reservationId) {
      dispatch(
        abortStockReservationAsyncThunk({
          body: { id: session?.checkoutData.reservationId },
          onSuccess: () => {
            setCurrentStep('cart');
          },
        })
      );
    } else {
      setCurrentStep('cart');
    }
  };

  const handlePaymentMethodSelect = (method: TPaymentMethod) => {
    setPaymentMethod(method);
    updateSessionData({ paymentMethod: method });
  };

  const handlePaymentComplete = (method: TPaymentMethod) => {
    // Here you would typically send the order to the backend
    console.log('Order completed:', {
      items: cartItems,
      shipping: shippingData,
      payment: method,
      total: subtotal + shippingCost,
    });

    // Clear cart after successful order
    // clearAll();
    // clearSession();

    // Redirect to order confirmation page or home
    // navigate(APP_ROUTES.ORDER_CONFIRMATION);
    alert(localize('checkout.demo.order.success') as string);
    // navigate(APP_ROUTES.HOME);
  };

  const handlePaymentBack = () => {
    updateSessionStep('shipping');
    setCurrentStep('shipping');
  };

  React.useEffect(() => {
    refreshFromStorage();
  }, [refreshFromStorage]);

  React.useEffect(() => {
    if (!session) {
      hasHydratedSessionRef.current = false;
      return;
    }

    if (isCheckoutSessionExpired(session)) {
      dispatch(clearCartInLocalCart());
      session.checkoutData.cartItems.forEach((item: TLocalCartItem) => {
        dispatch(addItemToLocalCart(item));
      });
      clearSession();
      dispatch(
        pushNotification({
          message: localize('checkout.notification.timeout') as string,
          type: NOTIFICATION_TYPES.WARNING,
        })
      );
      navigate(APP_ROUTES.HOME);
      return;
    }

    if (hasHydratedSessionRef.current) return;

    const { checkoutData } = session;
    setShippingData(checkoutData.shippingData);
    setPaymentMethod(checkoutData.paymentMethod);
    setCurrentStep(checkoutData.currentStep);
    hasHydratedSessionRef.current = true;
  }, [clearSession, dispatch, localize, navigate, session]);

  React.useEffect(() => {
    if (!session) return;
    if (currentStep === 'cart') return;

    const remainingMs = getCheckoutSessionRemainingMs(session);

    const abortCheckout = () => {
      if (!session || !isCheckoutSessionExpired(session)) return;

      dispatch(clearCartInLocalCart());
      session.checkoutData.cartItems.forEach((item: TLocalCartItem) => {
        dispatch(addItemToLocalCart(item));
      });
      clearSession();
      dispatch(
        pushNotification({
          message: localize('checkout.notification.timeout') as string,
          type: NOTIFICATION_TYPES.WARNING,
        })
      );
      navigate(APP_ROUTES.HOME);
    };

    if (remainingMs <= 0) {
      abortCheckout();
      return;
    }

    const timeoutId = window.setTimeout(abortCheckout, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clearSession, currentStep, dispatch, localize, navigate, session]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (cartItems.length === 0 && currentStep === 'cart') {
    return (
      <div className="checkout">
        <div className="checkout__container">
          <div className="checkout__empty">
            <h2>{localize('checkout.empty.title')}</h2>
            <p>{localize('checkout.empty.hint')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="checkout__container">
        <CheckoutProgress currentStep={currentStep} />

        <div className="checkout__content">
          {currentStep === 'cart' && (
            <CartReview
              items={cartItems}
              onQuantityChange={handleQuantityChange}
              onRemoveItem={handleRemoveItem}
              onContinue={handleCartContinue}
            />
          )}

          {currentStep === 'shipping' && (
            <ShippingForm
              initialData={shippingData || undefined}
              onContinue={handleShippingContinue}
              onBack={handleShippingBack}
            />
          )}

          {currentStep === 'payment' && (
            <PaymentMethod
              selectedMethod={paymentMethod}
              onMethodSelect={handlePaymentMethodSelect}
              onComplete={handlePaymentComplete}
              onBack={handlePaymentBack}
              subtotal={subtotal}
              shippingCost={shippingCost}
            />
          )}
        </div>
      </div>
    </div>
  );
};
